/**
 * Data Processing Agreement, version 1 (GDP-079, R1.9).
 *
 * Per CLAUDE.md §12 versioning rule: never edit a published version.
 * To revise, add `dpa-v2.ts` and bump `LEGAL_VERSIONS.dpa` in
 * `apps/api/src/legal/versions.ts`.
 */

export const DPA_VERSION = '2026-05-10-v1';

export const DPA_DE = `# Auftragsverarbeitungsvertrag (AVV)

**Version**: \`2026-05-10-v1\`
**Letzte Aktualisierung**: 10. Mai 2026

Dieser AVV ist integraler Bestandteil Deiner Nutzung der H³eroMap™-Plattform und ergänzt die Datenschutzerklärung. Er dokumentiert die Verarbeitung personenbezogener Daten durch SleepTrust Coaching ("Auftragsverarbeiter") und die technischen und organisatorischen Maßnahmen (TOMs).

Für Pilot-Klienten agiert SleepTrust Coaching als Verantwortliche und Hetzner / SendGrid / Sentry / OpenRouter als Auftragsverarbeiter. Du bestätigst diesen AVV im Rahmen Deiner Registrierung.

## 1. Gegenstand und Dauer

- **Gegenstand**: Verarbeitung personenbezogener Daten in der H³eroMap-Plattform.
- **Dauer**: Für die Dauer Deines Engagements zzgl. Aufbewahrungsfristen.
- **Datenkategorien**: Siehe Datenschutzerklärung §2.
- **Klientenkategorien**: Erwachsene, die heroisches Coaching wahrnehmen.

## 2. Pflichten des Auftragsverarbeiters

1. Verarbeitung nur auf dokumentierte Weisung.
2. Vertraulichkeitsverpflichtung autorisierter Personen.
3. Geeignete TOMs (siehe §4).
4. Unter-Auftragsverarbeiter nur mit Wissen des Verantwortlichen.
5. Unterstützung bei DSGVO Art. 32-36-Pflichten.
6. Bereitstellung aller Compliance-Nachweise.
7. Löschung oder Rückgabe aller Daten nach Engagement-Ende.

## 3. Unter-Auftragsverarbeiter

Liste in §6 der Datenschutzerklärung. 30 Tage Vorlaufzeit bei neuen Auftragsverarbeitern.

## 4. Technische und organisatorische Maßnahmen (TOMs)

- **Vertraulichkeit**: Bcrypt-Passwort-Hashing (Cost 12); RBAC; Audit-Log bei jedem Zugriff.
- **Integrität**: TLS 1.3 in der Übertragung; AES-256 im Ruhezustand; Checksum-Validierung.
- **Verfügbarkeit**: Tägliche verschlüsselte Backups (7-Tage-Rolling); 99,5 % Uptime-Ziel.
- **Belastbarkeit**: Disaster-Recovery-Prozedur; vierteljährliche Restore-Drills.
- **Pseudonymisierung**: Audit-Logs nutzen User-IDs; anonymisierte KI-Prompts.
- **Verschlüsselung**: TLS 1.3, AES-256, Zertifikats-Pinning in der macOS-Coach-App.
- **Zugriffskontrolle**: JWT-Tokens (kurzlebig) + Refresh-Tokens (rotiert).
- **Protokollierung**: Append-only-Audit-Trail (DB-Trigger + entzogene Schreib-GRANT).
- **Schwachstellen-Management**: Jährlicher externer Pen-Test; CI-Vulnerability-Scanning.
- **Incident Response**: Runbook; 72-Stunden-Meldung gemäß DSGVO Art. 33.

## 5. Meldung von Datenschutzverletzungen

Wir benachrichtigen innerhalb von 72 Stunden nach Kenntnis einer Verletzung mit Art, wahrscheinlichen Konsequenzen und ergriffenen Maßnahmen.

## 6. Deine Rechte als betroffene Person

Siehe Datenschutzerklärung §7 + §8.

## 7. Änderungen

Bei Aktualisierung: Version-Erhöhung, E-Mail-Zusammenfassung, erneute Zustimmung beim nächsten Login.

## 8. Kontakt

Fragen oder AVV-Anfragen: **support@sleepercising.com**.
`;

export const DPA_EN = `# Data Processing Agreement (DPA)

**Version**: \`2026-05-10-v1\`
**Last updated**: 10 May 2026

This DPA forms an integral part of your use of H³eroMap™ and complements the Privacy Policy. It documents the processing of personal data by SleepTrust Coaching ("Processor") and the technical and organisational measures (TOMs).

For pilot clients, SleepTrust Coaching acts as the data controller and Hetzner / SendGrid / Sentry / OpenRouter as sub-processors. You acknowledge this DPA as part of registration.

## 1. Subject matter and duration

- **Subject**: Processing of personal data submitted to the H³eroMap platform.
- **Duration**: For the duration of your engagement plus retention windows.
- **Data categories**: See Privacy Policy §2.
- **Client categories**: Adults engaging in heroic coaching.

## 2. Processor obligations

1. Process only on documented instructions.
2. Bind authorised personnel by confidentiality.
3. Implement appropriate TOMs (see §4).
4. Engage sub-processors only with the controller's knowledge.
5. Assist with GDPR Art. 32-36 obligations.
6. Make all information necessary to demonstrate compliance available.
7. Delete or return all personal data after the engagement ends.

## 3. Sub-processors

List in Privacy Policy §6. 30 days advance notice for new sub-processors.

## 4. Technical and organisational measures (TOMs)

- **Confidentiality**: bcrypt password hashing (cost 12); RBAC; audit log on all access.
- **Integrity**: TLS 1.3 in transit; AES-256 at rest; checksum validation on uploads.
- **Availability**: daily encrypted backups (7-day rolling); 99.5% uptime target.
- **Resilience**: documented disaster-recovery procedure; quarterly restore drills.
- **Pseudonymisation**: audit logs use user IDs; anonymised AI prompts.
- **Encryption**: TLS 1.3, AES-256, certificate pinning on the macOS coach app.
- **Access control**: JWT tokens (short-lived) + refresh tokens (rotated).
- **Logging**: append-only audit trail (DB trigger + revoked-write GRANT).
- **Vulnerability management**: annual external pen-test; dependency vulnerability scanning in CI.
- **Incident response**: documented runbook; 72-hour breach notification per GDPR Art. 33.

## 5. Data breach notification

We notify within 72 hours of becoming aware, including the nature of the breach, likely consequences, and measures taken.

## 6. Your rights as data subject

See Privacy Policy §7 + §8.

## 7. Changes

When updated: version bump, email summary, re-acknowledgement at next sign-in.

## 8. Contact

DPA-related questions: **support@sleepercising.com**.
`;

export const DPA_TEXT = {
  de: DPA_DE,
  en: DPA_EN,
} as const;
